/// Error diagnosis card displayed inline in the AI chat panel.
///
/// Shows a plain language explanation of the error, an expandable technical
/// details section, a primary "Apply Fix" button that loads the corrected
/// diagram via flowy:loadDocument, and alternative fix suggestions.

import { useState, useCallback } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { extractDiagramContent } from "@/components/ai/AIGeneratedDiagram";
import type { DiagramType } from "@/types/ai";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AIErrorDiagnosisProps {
  diagnosis: string;
  diagramType: DiagramType;
  tabId: string;
}

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

/** Extract plain language explanation (text before the first code block). */
function extractExplanation(diagnosis: string): string {
  const codeBlockIdx = diagnosis.indexOf("```");
  if (codeBlockIdx === -1) return diagnosis.trim();
  return diagnosis.slice(0, codeBlockIdx).trim();
}

/** Extract technical details: element ID, error type, spec reference. */
function extractTechnicalDetails(diagnosis: string): {
  elementId: string | null;
  errorType: string | null;
  specRef: string | null;
} {
  const elementIdMatch = diagnosis.match(
    /[Ee]lement\s+(?:ID|id)[:\s]+[`"]?(\S+?)[`"]?(?:\s|,|\.|$)/,
  );
  const errorTypeMatch = diagnosis.match(/[Ee]rror\s+(?:type|Type)[:\s]+[`"]?(.+?)[`"]?(?:\n|$)/);
  const specRefMatch = diagnosis.match(
    /(?:BPMN|spec|specification)\s+(?:reference|section)[:\s]+(.+?)(?:\n|$)/i,
  );

  return {
    elementId: elementIdMatch?.[1] ?? null,
    errorType: errorTypeMatch?.[1] ?? null,
    specRef: specRefMatch?.[1] ?? null,
  };
}

/** Extract alternative fix suggestions (text after the primary code block). */
function extractAlternatives(diagnosis: string): string[] {
  // Find the end of the first code block
  const firstBlockStart = diagnosis.indexOf("```");
  if (firstBlockStart === -1) return [];

  const afterFirst = diagnosis.slice(firstBlockStart + 3);
  const blockEnd = afterFirst.indexOf("```");
  if (blockEnd === -1) return [];

  const afterBlock = afterFirst.slice(blockEnd + 3).trim();
  if (!afterBlock) return [];

  // Split by numbered list items or bullet points
  const lines = afterBlock.split(/\n/).filter((line) => line.trim().length > 0);
  const alternatives: string[] = [];
  let currentAlt = "";

  for (const line of lines) {
    const trimmed = line.trim();
    // Detect numbered or bulleted items
    if (/^(?:\d+[.)]\s|[-*]\s)/.test(trimmed)) {
      if (currentAlt) alternatives.push(currentAlt.trim());
      currentAlt = trimmed.replace(/^(?:\d+[.)]\s|[-*]\s)/, "");
    } else {
      currentAlt += " " + trimmed;
    }
  }
  if (currentAlt) alternatives.push(currentAlt.trim());

  return alternatives.slice(0, 3); // Max 3 alternatives
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIErrorDiagnosis({ diagnosis, diagramType, tabId }: AIErrorDiagnosisProps) {
  const [showDetails, setShowDetails] = useState(false);

  const explanation = extractExplanation(diagnosis);
  const technicalDetails = extractTechnicalDetails(diagnosis);
  const alternatives = extractAlternatives(diagnosis);
  const fixContent = extractDiagramContent(diagnosis, diagramType);

  const handleApplyFix = useCallback(() => {
    if (!fixContent) return;

    // Dispatch loadDocument to apply the fix
    window.dispatchEvent(
      new CustomEvent("flowy:loadDocument", {
        detail: {
          content: fixContent,
          format: diagramType,
          tabId,
        },
      }),
    );

    // Show a notification about undo support
    // Using a simple approach: dispatch a custom notification event
    console.info("[AI Diagnosis] Fix applied -- use Ctrl+Z to undo");
  }, [fixContent, diagramType, tabId]);

  const hasTechnicalDetails =
    technicalDetails.elementId || technicalDetails.errorType || technicalDetails.specRef;

  return (
    <div className="my-2 rounded-lg border-l-4 border-l-orange-500 border border-zinc-700 bg-zinc-800/70 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-700/50">
        <AlertTriangle size={16} className="text-orange-400 flex-shrink-0" />
        <span className="text-sm text-zinc-200 font-medium">Error Diagnosis</span>
      </div>

      {/* Explanation */}
      <div className="px-3 py-2">
        <p className="text-sm text-zinc-300 whitespace-pre-wrap">{explanation}</p>
      </div>

      {/* Technical details (expandable) */}
      {hasTechnicalDetails && (
        <div className="border-t border-zinc-700/30">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors w-full text-left"
          >
            {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Technical Details
          </button>
          {showDetails && (
            <div className="px-3 pb-2 space-y-1">
              {technicalDetails.elementId && (
                <p className="text-xs text-zinc-500">
                  <span className="text-zinc-400">Element ID:</span>{" "}
                  <code className="font-mono text-zinc-300">{technicalDetails.elementId}</code>
                </p>
              )}
              {technicalDetails.errorType && (
                <p className="text-xs text-zinc-500">
                  <span className="text-zinc-400">Error Type:</span> {technicalDetails.errorType}
                </p>
              )}
              {technicalDetails.specRef && (
                <p className="text-xs text-zinc-500">
                  <span className="text-zinc-400">Spec Reference:</span> {technicalDetails.specRef}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Primary fix */}
      {fixContent ? (
        <div className="border-t border-zinc-700/30 px-3 py-2">
          <button
            type="button"
            onClick={handleApplyFix}
            className="flex items-center gap-1.5 rounded-md bg-green-600 hover:bg-green-500 px-3 py-1.5 text-xs text-white font-medium transition-colors"
          >
            <Wrench size={14} />
            Apply Fix
          </button>
        </div>
      ) : (
        <div className="border-t border-zinc-700/30 px-3 py-2">
          <p className="text-xs text-zinc-500 italic">Ask AI for a specific fix in the chat.</p>
        </div>
      )}

      {/* Alternative fixes */}
      {alternatives.length > 0 && (
        <div className="border-t border-zinc-700/30 px-3 py-2">
          <p className="text-xs text-zinc-400 font-medium mb-1">Alternative suggestions:</p>
          <ol className="text-xs text-zinc-500 space-y-0.5 list-decimal pl-4">
            {alternatives.map((alt, idx) => (
              <li key={idx}>{alt}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
